/**
 * The financial-statement card (E2.1 design spec 11.4): replaces the old
 * 6-KPI-card row -- "card overload" ruling, "the seven figures are one
 * story". Rows mirror the owner's spreadsheet: Gross sales / less Payout
 * (payout % inline) / = Gross profit (GP% inline) / less Tax / = Net
 * profit, tabular-nums amounts right-aligned. Delta chips + comparison
 * context (E2.1 spec 11.2) land on Gross sales, Gross profit and Net profit
 * -- the spec's explicit list -- plus Payout (kept rather than dropped: the
 * spec calls it optional and the inverted coloring is meaningful there
 * too). Tax gets no chip: it's a pass-through of the tax_rate_bp config,
 * not its own "is this good news" story, and the spec doesn't name it.
 */
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';

import type { CurrencySummary } from '../../api/index.ts';
import { MoneyText } from '../../components/MoneyText.tsx';
import { MONEY_NUMERIC_SX, palette } from '../../theme.ts';
import { DeltaChip } from './DeltaChip.tsx';
import { type DeltaMetricKey, grossProfitPercentText, payoutRatioCaption } from './dashboardHelpers.ts';
import { StatementCardSkeleton } from './dashboardSkeletons.tsx';
import type { ComparisonContext } from './periodState.ts';

function rowPrefixText(prefix: StatementRowProps['prefix']): string {
  if (prefix === 'less') return 'less ';
  if (prefix === '=') return '= ';
  return '';
}

interface StatementRowProps {
  prefix?: 'less' | '=';
  label: string;
  minor: number;
  currencyCode: string | null;
  inlineText?: string;
  metric?: DeltaMetricKey;
  previousMinor?: number | null;
  /** The chip's current-side figure when it differs from `minor` (the
   *  day-capped MTD basis); defaults to `minor`. */
  chipCurrentMinor?: number;
  comparisonContext?: ComparisonContext;
  emphasize?: boolean;
  divider?: boolean;
}

function StatementRow({
  prefix,
  label,
  minor,
  currencyCode,
  inlineText,
  metric,
  previousMinor,
  chipCurrentMinor,
  comparisonContext,
  emphasize = false,
  divider = false,
}: Readonly<StatementRowProps>): React.JSX.Element {
  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        py: 1.25,
        borderTop: divider ? `1px solid ${palette.quietBorder}` : 'none',
        gap: 1,
      }}
    >
      <Box>
        <Typography sx={{ fontWeight: emphasize ? 800 : 600, color: emphasize ? palette.textPrimary : palette.textSecondary }}>
          {rowPrefixText(prefix)}
          {label}
        </Typography>
        {metric && comparisonContext && (
          <DeltaChip
            metric={metric}
            current={chipCurrentMinor ?? minor}
            previous={previousMinor ?? null}
            contextLabel={comparisonContext.label}
            tooltipText={comparisonContext.tooltipText}
          />
        )}
      </Box>
      <Box sx={{ textAlign: 'right' }}>
        <MoneyText minor={minor} currencyCode={currencyCode} size={emphasize ? '1.5rem' : '1.15rem'} weight={emphasize ? 800 : 700} />
        {inlineText && (
          <Typography variant="caption" sx={{ ...MONEY_NUMERIC_SX, color: palette.textSecondary, display: 'block' }}>
            {inlineText}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

export interface StatementCardProps {
  currentSummary: CurrencySummary;
  previousSummary: CurrencySummary;
  /** The CURRENT side the delta chips compare: the
   *  day-capped summary for a month-to-date selection, so both chip sides
   *  span the same complete days while the rows still show the uncapped MTD
   *  `currentSummary`. Omitted = the chips compare `currentSummary`. */
  chipCurrentSummary?: CurrencySummary;
  currencyCode: string | null;
  comparisonContext: ComparisonContext;
  /** FIRST load only (owner ruling 2026-07-15): the representative skeleton
   *  renders instead of the card; later reloads keep showing the last
   *  figures, so the caller must not set this once real data has landed. */
  loading?: boolean;
  /** False when the selected period has NO reports (owner rulings 2026-08-12 +
   *  2026-08-18): every figure is a structural zero, so "-100% vs <prev>" is
   *  pure noise; the header carries no empty-state line, the zeros tell it. */
  showDeltas?: boolean;
  /** False = the External/government barebones statement (owner ruling
   *  2026-08-12): gross sales, payout (no ratio caption) and tax only.
   *  Presentational, NOT a data gate -- net profit is derivable arithmetic. */
  detailed?: boolean;
}

export function StatementCard({
  currentSummary,
  previousSummary,
  chipCurrentSummary,
  currencyCode,
  comparisonContext,
  loading = false,
  showDeltas = true,
  detailed = true,
}: Readonly<StatementCardProps>): React.JSX.Element {
  if (loading) return <StatementCardSkeleton />;
  const chipCurrent = chipCurrentSummary ?? currentSummary;
  const grossProfitPct = grossProfitPercentText(currentSummary.gross_profit_minor, currentSummary.gross_sales_minor);
  // Every row's chip is gated by comparisonContext, so withholding it here
  // silences all five at once.
  const rowContext = showDeltas ? comparisonContext : undefined;

  // Full height only at md+, where the card shares a stretched row; at xs it
  // would stretch into the stacked column's pixel flex-base (mirrored in
  // dashboardSkeletons.tsx's ghost).
  return (
    <Paper sx={{ p: 2.5, width: '100%', height: { xs: 'auto', md: '100%' }, boxSizing: 'border-box' }}>
      <Typography variant="h6" sx={{ mb: 0.5 }}>
        Financial statement
      </Typography>
      <StatementRow
        label="Gross sales"
        minor={currentSummary.gross_sales_minor}
        currencyCode={currencyCode}
        metric="gross_sales"
        previousMinor={previousSummary.gross_sales_minor}
        chipCurrentMinor={chipCurrent.gross_sales_minor}
        comparisonContext={rowContext}
        emphasize
      />
      <StatementRow
        prefix="less"
        label="Payout"
        minor={currentSummary.payout_minor}
        currencyCode={currencyCode}
        inlineText={
          detailed
            ? payoutRatioCaption(currentSummary.payout_minor, currentSummary.gross_sales_minor, currentSummary.tax_total_minor)
            : undefined
        }
        metric="payout"
        previousMinor={previousSummary.payout_minor}
        chipCurrentMinor={chipCurrent.payout_minor}
        comparisonContext={rowContext}
        divider
      />
      {detailed && (
        <StatementRow
          prefix="="
          label="Gross profit"
          minor={currentSummary.gross_profit_minor}
          currencyCode={currencyCode}
          inlineText={grossProfitPct}
          metric="gross_profit"
          previousMinor={previousSummary.gross_profit_minor}
          chipCurrentMinor={chipCurrent.gross_profit_minor}
          comparisonContext={rowContext}
          emphasize
          divider
        />
      )}
      <StatementRow prefix="less" label="Tax" minor={currentSummary.tax_total_minor} currencyCode={currencyCode} divider />
      {detailed && (
        <StatementRow
          prefix="="
          label="Net profit"
          minor={currentSummary.profit_minor}
          currencyCode={currencyCode}
          metric="net_profit"
          previousMinor={previousSummary.profit_minor}
          chipCurrentMinor={chipCurrent.profit_minor}
          comparisonContext={rowContext}
          emphasize
          divider
        />
      )}
    </Paper>
  );
}

export default StatementCard;
